import type { LucideIcon } from "lucide-react";
import type { ComponentProps, ReactNode, Ref } from "react";
import { reviewContentForDisplay } from "../../lib/review-display";
import { reviewTextForDisplay } from "../../lib/review-text";
import { cn } from "../../lib/utils";
import { useTaskDialog } from "../shared-ui";
import { Avatar, AvatarFallback } from "../ui/avatar";
import { Badge } from "../ui/badge";
import { Card, CardAction, CardHeader, CardTitle } from "../ui/card";
import { Input } from "../ui/input";
import { Progress } from "../ui/progress";
import { Slider } from "../ui/slider";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../ui/tabs";

/** Shared Cyra page compositions. */
export function DashboardPage({ className, ...props }: ComponentProps<"section">) {
  return (
    <section
      className={cn("flex min-w-0 flex-col gap-3", className)}
      data-ui="task-view"
      {...props}
    />
  );
}

export function DashboardGrid({ children }: { children: ReactNode }) {
  return (
    <div className="grid items-start gap-4 lg:grid-cols-2 [&>*:only-child]:col-span-full">
      {children}
    </div>
  );
}

/** One quiet summary strip, without a separate card and action for every number. */
export function MetricStrip({ items }: { items: Array<{ label: string; value: ReactNode }> }) {
  return (
    <dl className="grid grid-cols-[repeat(auto-fit,minmax(min(100%,8rem),1fr))] gap-x-4 gap-y-3 border-y py-3">
      {items.map(({ label, value }) => (
        <div className="min-w-0" key={label}>
          <dt className="text-xs text-muted-foreground">{label}</dt>
          <dd className="mt-1 min-w-0 text-lg font-semibold tabular-nums">{value}</dd>
        </div>
      ))}
    </dl>
  );
}

export function EntityHeader({
  actions,
  fallback,
  meta,
  name,
  status,
}: {
  actions?: ReactNode;
  fallback: string;
  meta: ReactNode;
  name: ReactNode;
  status?: ReactNode;
}) {
  const inTaskDialog = useTaskDialog();
  return (
    <Card className="border-0 bg-transparent py-0 shadow-none">
      <CardHeader
        className={cn("items-center px-0 sm:grid-cols-[1fr_auto]", inTaskDialog && "pr-8")}
      >
        <div className="flex min-w-0 items-center gap-3">
          <Avatar className="size-9 rounded-lg grayscale">
            <AvatarFallback className="rounded-lg">{reviewTextForDisplay(fallback)}</AvatarFallback>
          </Avatar>
          <div className="min-w-0">
            <CardTitle className="flex flex-wrap items-center gap-2 text-base">
              <h2>{reviewContentForDisplay(name)}</h2>
              {status}
            </CardTitle>
            <div className="truncate text-sm text-muted-foreground">
              {reviewContentForDisplay(meta)}
            </div>
          </div>
        </div>
        {actions ? (
          <CardAction className="static col-start-auto row-start-auto">{actions}</CardAction>
        ) : null}
      </CardHeader>
    </Card>
  );
}

export function DashboardCard({
  action,
  children,
  framed = false,
  label,
  title,
}: {
  action?: ReactNode;
  children: ReactNode;
  framed?: boolean;
  label?: string;
  title: ReactNode;
}) {
  return (
    <section
      aria-label={label ? reviewTextForDisplay(label) : label}
      className={cn("@container/card min-w-0", framed ? "cyra-card" : "space-y-2")}
      role={label ? "region" : undefined}
    >
      <header className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="text-sm font-medium">{reviewContentForDisplay(title)}</h2>
        {action}
      </header>
      <div className={cn("min-w-0", framed && "flex flex-col gap-4")}>{children}</div>
    </section>
  );
}

export function ActionRow({ children, end = false }: { children: ReactNode; end?: boolean }) {
  return (
    <div className={cn("flex flex-wrap items-center gap-1.5", end && "justify-end")}>
      {children}
    </div>
  );
}

export function FormStack(props: ComponentProps<"form">) {
  return <form className="flex min-w-0 flex-col gap-3" {...props} />;
}

export function FilterForm(props: ComponentProps<"form">) {
  return <form className="flex flex-col gap-3 sm:flex-row sm:items-center" {...props} />;
}

export function ContentStack({
  children,
  readable = false,
}: {
  children: ReactNode;
  readable?: boolean;
}) {
  return (
    <div className={cn("flex min-w-0 flex-col gap-4", readable && "max-w-4xl")}>{children}</div>
  );
}

export function CompactGrid({
  children,
  scroll = false,
}: {
  children: ReactNode;
  scroll?: boolean;
}) {
  return (
    <div
      className={cn(
        "grid min-w-0 grid-cols-[repeat(auto-fit,minmax(min(100%,15rem),1fr))] gap-x-4 gap-y-2 text-sm",
        scroll && "max-h-64 overflow-y-auto rounded-md border p-2",
      )}
    >
      {children}
    </div>
  );
}

export function CompactPanel({ children }: { children: ReactNode }) {
  return <div className="min-w-0 rounded-md border bg-muted/25 p-3">{children}</div>;
}

export function CompactPanelHeader({ children }: { children: ReactNode }) {
  return <div className="flex items-center justify-between gap-2">{children}</div>;
}

export function CompactMetadata({
  children,
  truncate = false,
}: {
  children: ReactNode;
  truncate?: boolean;
}) {
  return <p className={cn("text-sm text-muted-foreground", truncate && "truncate")}>{children}</p>;
}

export function DashboardItem({ children }: { children: ReactNode }) {
  return <div className="border-b py-2 last:border-b-0">{children}</div>;
}

export function DashboardItemHeader({ children }: { children: ReactNode }) {
  return <div className="flex flex-wrap items-center justify-between gap-2">{children}</div>;
}

export function DetailChange({ children, label }: { children: ReactNode; label: ReactNode }) {
  return (
    <div className="grid gap-1 text-sm md:grid-cols-[12rem_1fr]">
      <span className="font-medium">{reviewContentForDisplay(label)}</span>
      <div className="min-w-0 wrap-anywhere">{reviewContentForDisplay(children)}</div>
    </div>
  );
}

export function FeatureIcon({ icon: Icon }: { icon: LucideIcon }) {
  return <Icon aria-hidden="true" className="size-4" />;
}

export function ChoiceGroup({ children, legend }: { children: ReactNode; legend: string }) {
  return (
    <fieldset className="flex flex-col gap-3">
      <legend className="text-sm font-medium">{legend}</legend>
      {children}
    </fieldset>
  );
}

export function CompactChoiceGroup({ children, legend }: { children: ReactNode; legend: string }) {
  return (
    <fieldset className="min-w-0">
      <legend className="mb-2 text-sm font-medium">{reviewTextForDisplay(legend)}</legend>
      <CompactGrid scroll>{children}</CompactGrid>
    </fieldset>
  );
}

export function FormGrid({ children }: { children: ReactNode }) {
  return (
    <div className="grid min-w-0 grid-cols-[repeat(auto-fit,minmax(min(100%,17rem),1fr))] items-start gap-x-4 gap-y-3">
      {children}
    </div>
  );
}

export function FormActions({ children }: { children: ReactNode }) {
  return (
    <div
      data-ui="form-actions"
      className="sticky bottom-[var(--form-actions-bottom,0px)] mb-[var(--form-actions-bottom,0px)] z-10 flex flex-wrap items-center justify-end gap-2 border-t bg-background py-3"
    >
      {children}
    </div>
  );
}

export function DetailList({
  items,
}: {
  items: Array<{ key?: string; label: string; value: ReactNode; wide?: boolean }>;
}) {
  return (
    <dl className="grid grid-cols-[repeat(auto-fit,minmax(min(100%,15rem),1fr))] gap-x-4 gap-y-2 text-sm">
      {items.map((item) => (
        <div className={cn("min-w-0", item.wide && "col-span-full")} key={item.key ?? item.label}>
          <dt className="text-xs text-muted-foreground">{reviewTextForDisplay(item.label)}</dt>
          <dd className={cn("min-w-0 wrap-anywhere", item.wide && "whitespace-pre-wrap")}>
            {reviewContentForDisplay(item.value)}
          </dd>
        </div>
      ))}
    </dl>
  );
}

/** Read-only metadata pairs; these are record properties, not a sortable collection. */
export function MetadataTable({
  items,
  label = "Metadata",
}: {
  items: Array<{ label: string; value: ReactNode }>;
  label?: string;
}) {
  return (
    <div className="overflow-hidden rounded-md border bg-card">
      <table className="cyra-metadata-table">
        <caption className="sr-only">{label}</caption>
        <tbody>
          {items.map((item) => (
            <tr key={item.label}>
              <th scope="row">{reviewTextForDisplay(item.label)}</th>
              <td>{reviewContentForDisplay(item.value)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function LifecycleTimeline({
  items,
}: {
  items: Array<{ label: string; timestamp: ReactNode }>;
}) {
  return (
    <ol className="relative ml-2 border-l">
      {items.map((item) => (
        <li
          className="relative pb-5 pl-5 last:pb-0"
          key={`${item.label}-${String(item.timestamp)}`}
        >
          <span className="absolute -left-1 top-1.5 size-2 rounded-full bg-primary" />
          <span className="block text-sm font-medium">{item.label}</span>
          <span className="block text-xs text-muted-foreground">{item.timestamp}</span>
        </li>
      ))}
    </ol>
  );
}

export function SecondaryText({ children }: { children: ReactNode }) {
  return (
    <span className="block text-xs text-muted-foreground wrap-anywhere">
      {reviewContentForDisplay(children)}
    </span>
  );
}

export function MonoValue({ children }: { children: ReactNode }) {
  return (
    <span className="font-mono text-xs text-muted-foreground wrap-anywhere">
      {reviewContentForDisplay(children)}
    </span>
  );
}

export function MediaPreview({
  alt,
  fallback,
  imageRef,
  onError,
  src,
}: {
  alt: string;
  fallback: ReactNode;
  imageRef?: Ref<HTMLImageElement> | undefined;
  onError?: (() => void) | undefined;
  src?: string | undefined;
}) {
  return (
    <div className="flex aspect-square w-full max-w-sm items-center justify-center overflow-hidden rounded-xl border bg-muted">
      {src ? (
        <img
          alt={alt}
          className="size-full object-cover"
          onError={onError}
          ref={imageRef}
          referrerPolicy="no-referrer"
          src={src}
        />
      ) : (
        <span className="text-4xl font-semibold text-muted-foreground">{fallback}</span>
      )}
    </div>
  );
}

export function DetailTabs({
  actions,
  label = "Detail sections",
  children,
  onValueChange,
  options,
  value,
}: {
  actions?: ReactNode;
  label?: string;
  children: ReactNode;
  onValueChange: (value: string) => void;
  options: Array<{ label: string; value: string; icon?: LucideIcon }>;
  value: string;
}) {
  return (
    <Tabs className="gap-0" onValueChange={onValueChange} value={value}>
      <div className="flex min-w-0 items-end justify-between gap-3 border-b">
        <TabsList
          aria-label={label}
          className="-mb-px h-auto! min-w-0 flex-nowrap justify-start gap-3 overflow-x-auto rounded-none bg-transparent p-0"
        >
          {options.map((option) => (
            <TabsTrigger
              className="h-9 flex-none rounded-none border-0 border-b-2 border-transparent bg-transparent px-0 shadow-none data-[state=active]:border-foreground data-[state=active]:bg-transparent data-[state=active]:shadow-none dark:data-[state=active]:bg-transparent"
              key={option.value}
              value={option.value}
            >
              {option.icon ? <option.icon aria-hidden="true" className="size-4" /> : null}
              {option.label}
            </TabsTrigger>
          ))}
        </TabsList>
        {actions ? <div className="ml-auto shrink-0">{actions}</div> : null}
      </div>
      <TabsContent className="mt-3 min-w-0" value={value}>
        {children}
      </TabsContent>
    </Tabs>
  );
}

export function RangeField({
  id,
  label,
  onValueChange,
  value,
}: {
  id: string;
  label: string;
  onValueChange: (value: number[]) => void;
  value: number[];
}) {
  return (
    <label className="flex flex-col gap-2" htmlFor={id}>
      <span className="text-sm font-medium">{label}</span>
      <Slider
        aria-label={label}
        id={id}
        max={100}
        min={0}
        onValueChange={onValueChange}
        value={value}
      />
    </label>
  );
}

export function ProgressLine({ label, value }: { label: string; value: number }) {
  return (
    <ContentStack>
      <div className="flex items-center justify-between gap-5">
        <strong>{label}</strong>
        <Badge variant="secondary">{value}%</Badge>
      </div>
      <Progress aria-label={label} value={value} />
    </ContentStack>
  );
}

export function HiddenFileInput(props: ComponentProps<typeof Input>) {
  return <Input {...props} hidden tabIndex={-1} />;
}

export function RichDocument({ html }: { html: string }) {
  return (
    <div
      className="max-w-none leading-7 text-foreground [&_a]:text-primary [&_a]:underline [&_h2]:mt-6 [&_h2]:text-2xl [&_h2]:font-semibold [&_h3]:mt-5 [&_h3]:text-xl [&_h3]:font-semibold [&_li]:ml-6 [&_ol]:list-decimal [&_p]:my-3 [&_ul]:list-disc"
      // biome-ignore lint/security/noDangerouslySetInnerHtml: The legal boundary passes only allowlist-sanitized HTML.
      dangerouslySetInnerHTML={{ __html: html }}
    />
  );
}
